<script setup lang="ts">
import {ref, nextTick, onMounted, onUnmounted} from 'vue';
import {GET, POST} from '../modules/fetch.ts';
import {renderPreviewPanelContent} from '../features/repo-editor.ts';

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: number;
  turnId: number;
  role: ChatRole;
  status: string;
  content: string;
  // Transient tool activity, live from SSE, not persisted: the tool DRCY is
  // currently running, and how many tools ran this turn (shown as a summary
  // once the turn finishes).
  activity: string;
  toolCount: number;
};

// Wire shape from the Hub (api.DrcyChatMessageEntry) and SSE event payloads.
type MessageEntry = {
  id?: number;
  turn_id?: number;
  role: ChatRole;
  status?: string;
  content?: string;
};

const props = withDefaults(
  defineProps<{
    // Chat scope: "pr:<index>" (a design review) or "repo:<branch>".
    scope: string;
    repoLink: string;
    drcyAvatar: string;
    thinkingSrc?: string;
    userName?: string;
    userAvatar?: string;
    userHomeLink?: string;
  }>(),
  {thinkingSrc: '', userName: 'You', userAvatar: '', userHomeLink: ''},
);

const messages = ref<ChatMessage[]>([]);
const scroller = ref<HTMLElement>();

let eventSource: EventSource | null = null;

// Fallback poll while a turn is in flight. SSE is the primary channel, but a
// silently-dead-but-"open" stream (server restart, proxy timeout, sleep/wake)
// never fires 'open'/'error', so the reconnect catch-up can't run and the turn
// would spin forever. Polling history reconciles content and terminal status
// regardless of the stream's health, then stops once nothing is in flight.
let pollTimer: number | null = null;
const POLL_INTERVAL_MS = 3000;

// Last emitted busy state, so we only notify the composer on transitions.
let lastBusy = false;

// Message bodies we render markdown into, keyed by message id, plus per-message
// debounce timers so streaming re-renders don't hammer the server.
const bubbleEls = new Map<number, HTMLElement>();
const renderTimers = new Map<number, number>();
const RENDER_DEBOUNCE_MS = 350;
// Content most recently rendered (or dispatched to /markup) per message id, so
// we skip re-rendering unchanged content (e.g. every 3s poll re-upserts every
// message) and can discard a stale /markup response.
const renderedContent = new Map<number, string>();

const baseUrl = `${props.repoLink}/drcy_chat`;
const scopeQuery = `scope=${encodeURIComponent(props.scope)}`;

function authorName(message: ChatMessage): string {
  return message.role === 'assistant' ? 'DRCY' : props.userName;
}
function avatarSrc(message: ChatMessage): string {
  return message.role === 'assistant' ? props.drcyAvatar : props.userAvatar;
}
function avatarHref(message: ChatMessage): string | undefined {
  return message.role === 'assistant' ? undefined : props.userHomeLink || undefined;
}

function upsert(entry: MessageEntry) {
  if (!entry.id) return;
  const existing = messages.value.find((m) => m.id === entry.id);
  const next: ChatMessage = {
    id: entry.id,
    turnId: entry.turn_id ?? 0,
    role: entry.role,
    status: entry.status ?? '',
    content: entry.content ?? '',
    activity: existing?.activity ?? '',
    toolCount: existing?.toolCount ?? 0,
  };
  const index = messages.value.findIndex((m) => m.id === next.id);
  if (index >= 0) {
    const prev = messages.value[index];
    // While a turn streams, the SSE 'chunk' handler grows the assistant content
    // in place. A concurrent history poll can carry an older, shorter snapshot;
    // merging it would truncate the live text and the next chunk would append
    // onto that stale prefix (garbled output). So for an in-flight assistant,
    // never let a merge shrink the content — SSE owns it until the turn ends,
    // at which point the terminal snapshot is at least as long and applies.
    const inFlightAssistant =
      prev.role === 'assistant' && (prev.status === 'pending' || prev.status === 'running');
    if (inFlightAssistant && next.content.length < prev.content.length) {
      next.content = prev.content;
    }
    messages.value[index] = {...prev, ...next};
  } else {
    messages.value.push(next);
    messages.value.sort((a, b) => a.id - b.id);
  }
  if (next.content) renderNow(messages.value.find((m) => m.id === next.id));
  scrollToBottom();
  syncPolling();
}

function assistantForTurn(turnId: number): ChatMessage | undefined {
  return messages.value.find((m) => m.turnId === turnId && m.role === 'assistant');
}

function setBubble(id: number, el: Element | null) {
  if (el) bubbleEls.set(id, el as HTMLElement);
  else bubbleEls.delete(id);
}

// Render a message's markdown through the Hub's own markup pipeline (server
// render + the global `.markup` enhancers: KaTeX, code highlight, mermaid) —
// the same path comment previews use, so chat content looks like comments.
async function renderMarkdown(message: ChatMessage | undefined) {
  if (!message || !message.content) return;
  // Snapshot the content we are about to render; if it changes while we await
  // /markup, a newer render will run and this (stale) response is discarded so
  // it can't overwrite the bubble with older partial content.
  const content = message.content;
  renderedContent.set(message.id, content);
  await nextTick();
  const el = bubbleEls.get(message.id);
  if (!el) return;
  try {
    const formData = new FormData();
    formData.append('mode', 'comment');
    formData.append('context', props.repoLink);
    formData.append('text', content);
    const res = await POST(`${props.repoLink}/markup`, {data: formData});
    if (message.content !== content) return; // superseded while awaiting
    if (!res.ok) {
      el.textContent = content;
      return;
    }
    const html = await res.text();
    if (message.content !== content) return; // superseded while awaiting
    renderPreviewPanelContent(el, html);
  } catch (err) {
    console.error('Failed to render DRCY markdown', err);
    if (message.content === content) el.textContent = content;
  }
  scrollToBottom();
}

function scheduleRender(message: ChatMessage | undefined) {
  // Skip when the current content is already what we last rendered/dispatched,
  // so repeated upserts of unchanged content (every poll tick) don't re-render.
  if (!message || renderedContent.get(message.id) === message.content) return;
  const existing = renderTimers.get(message.id);
  if (existing) clearTimeout(existing);
  renderTimers.set(
    message.id,
    window.setTimeout(() => {
      renderTimers.delete(message.id);
      renderMarkdown(message);
    }, RENDER_DEBOUNCE_MS),
  );
}

function renderNow(message: ChatMessage | undefined) {
  if (!message || renderedContent.get(message.id) === message.content) return;
  const existing = renderTimers.get(message.id);
  if (existing) {
    clearTimeout(existing);
    renderTimers.delete(message.id);
  }
  renderMarkdown(message);
}

// How close to the bottom (px) still counts as "following" the conversation.
const SCROLL_FOLLOW_THRESHOLD_PX = 80;

function isNearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_FOLLOW_THRESHOLD_PX;
}

function scrollToBottom() {
  // Respect a user who scrolled up to read earlier messages: only auto-follow
  // new content when they're already near the bottom. Checked synchronously
  // (before nextTick) so it reflects the position before this update rendered.
  const el = scroller.value;
  if (el && !isNearBottom(el)) return;
  nextTick(() => {
    const target = scroller.value;
    if (target) target.scrollTop = target.scrollHeight;
  });
}

function clearAll() {
  for (const timer of renderTimers.values()) clearTimeout(timer);
  renderTimers.clear();
  renderedContent.clear();
  bubbleEls.clear();
  messages.value = [];
  syncPolling();
}

// Export the conversation as a Markdown file. Client-side only — the messages
// are already in memory — so no design data leaves the browser beyond what the
// user chooses to save.
function exportMarkdown() {
  const lines: string[] = [
    `# Chat with DRCY: ${props.repoLink} (${props.scope})`,
    '',
  ];
  for (const message of messages.value) {
    const body = message.content.trim();
    if (!body) continue;
    lines.push(`**${authorName(message)}**`, '', body, '', '---', '');
  }
  const blob = new Blob([lines.join('\n')], {type: 'text/markdown'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `drcy-chat-${props.scope.replace(/[^a-z0-9]+/gi, '-')}.md`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Open the browser print dialog. A scoped print stylesheet isolates the chat so
// "Save as PDF" produces a clean transcript with the rendered answers.
function printChat() {
  window.print();
}

// Example questions shown on the empty landing state. Clicking one prefills the
// composer (which lives in the page template, outside this component) via an
// event the glue listens for, so the user can review and send.
const examplePrompts = [
  'What does this design do?',
  'List the power rails and their sources.',
  'What are the main ICs and their functions?',
];
function prefill(text: string) {
  document.dispatchEvent(new CustomEvent('drcy-chat-prefill', {detail: text}));
}

async function loadHistory() {
  try {
    const res = await GET(`${baseUrl}/history?${scopeQuery}`);
    if (!res.ok) return;
    const data = await res.json();
    for (const entry of data.messages ?? []) upsert(entry);
  } catch (err) {
    console.error('Failed to load DRCY chat history', err);
  }
}

function hasInFlightTurn(): boolean {
  return messages.value.some(
    (m) => m.role === 'assistant' && (m.status === 'pending' || m.status === 'running'),
  );
}

// Start polling when a turn is in flight, stop when none is; also tell the
// composer (which lives in the page template, outside this component) to disable
// send while a turn responds and re-enable it once done. Idempotent, so it's
// safe to call after any state change (SSE event, upsert, or a poll tick).
function syncPolling() {
  const busy = hasInFlightTurn();
  if (busy) {
    if (pollTimer === null) {
      pollTimer = window.setInterval(loadHistory, POLL_INTERVAL_MS);
    }
  } else if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (busy !== lastBusy) {
    lastBusy = busy;
    document.dispatchEvent(new CustomEvent('drcy-chat-busy', {detail: busy}));
  }
}

function openStream() {
  eventSource = new EventSource(`${baseUrl}/stream?${scopeQuery}`);
  eventSource.addEventListener('open', () => {
    // Reconcile history on every open, including the first. Because the stream is
    // subscribed before this catch-up read, no message posted during the read is
    // missed (it is delivered live), and reconnects (server restart, network
    // blip) also catch up instead of hanging on a stale turn.
    loadHistory();
  });
  eventSource.addEventListener('message', (event: MessageEvent) => {
    upsert(JSON.parse(event.data) as MessageEntry);
  });
  eventSource.addEventListener('chunk', (event: MessageEvent) => {
    const {turn_id: turnId, content} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant) {
      assistant.content += content;
      if (assistant.status === 'pending') assistant.status = 'running';
      scheduleRender(assistant);
      scrollToBottom();
    }
  });
  eventSource.addEventListener('activity', (event: MessageEvent) => {
    const {turn_id: turnId, label} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant && label) {
      // Show only the tool running right now; keep a running count for the
      // end-of-turn summary.
      assistant.activity = label;
      assistant.toolCount += 1;
      scrollToBottom();
    }
  });
  eventSource.addEventListener('status', (event: MessageEvent) => {
    const {turn_id: turnId, status, content} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant) {
      assistant.status = status;
      if (content) assistant.content = content;
      if (status === 'completed' || status === 'failed') {
        assistant.activity = ''; // stop the live tool line; the count remains
        renderNow(assistant);
      } else {
        scheduleRender(assistant);
      }
      scrollToBottom();
      syncPolling();
    }
  });
  eventSource.addEventListener('cleared', () => clearAll());
  eventSource.addEventListener('error', () => {
    // EventSource auto-reconnects on a transient error (and its 'open' handler
    // re-runs the history catch-up). Until it recovers — or if it can't — fall
    // back to the history poll so an in-flight turn still reconciles and reaches
    // a terminal state instead of spinning. syncPolling is a no-op when nothing
    // is in flight, so this costs nothing on an idle stream.
    syncPolling();
  });
}

// The composer glue (features/drcy-chat.ts) lives outside this component and
// coordinates through the server, but on a successful send/end it also signals us
// directly so the view updates even when SSE is dead or on another Hub process:
// 'reconcile' reloads history (showing the new turn and starting the fallback
// poll), 'ended' clears the transcript locally.
function onReconcile() {
  loadHistory();
}
function onEnded() {
  clearAll();
}

onMounted(() => {
  // Open the stream first; its 'open' handler does the initial history load, so
  // the subscription is active before the catch-up read (no missed-message gap).
  openStream();
  document.addEventListener('drcy-chat-reconcile', onReconcile);
  document.addEventListener('drcy-chat-ended', onEnded);
});

onUnmounted(() => {
  if (eventSource) eventSource.close();
  if (pollTimer !== null) clearInterval(pollTimer);
  for (const timer of renderTimers.values()) clearTimeout(timer);
  renderTimers.clear();
  document.removeEventListener('drcy-chat-reconcile', onReconcile);
  document.removeEventListener('drcy-chat-ended', onEnded);
});
</script>

<template>
  <div class="drcy-chat">
    <div v-if="messages.length" class="drcy-chat-toolbar tw:flex tw:justify-end tw:gap-2 tw:mb-2">
      <button class="ui tiny basic button" type="button" @click="exportMarkdown">
        Export Markdown
      </button>
      <button class="ui tiny basic button" type="button" @click="printChat">
        Save as PDF
      </button>
    </div>
    <div ref="scroller" class="drcy-chat-messages">
    <div v-if="messages.length === 0" class="drcy-chat-empty">
      <img :src="drcyAvatar" class="drcy-chat-empty-avatar" alt="">
      <div class="drcy-chat-empty-title">Chat with DRCY about this design</div>
      <div class="drcy-chat-empty-subtitle">
        Ask about components, nets, connections, or part specs. Every answer is backed by evidence you can check.
      </div>
      <div class="drcy-chat-empty-examples">
        <button
          v-for="(example, i) in examplePrompts"
          :key="i"
          type="button"
          class="ui basic button drcy-chat-example"
          @click="prefill(example)"
        >{{ example }}</button>
      </div>
    </div>
    <div v-else class="comment-list">
     <div class="timeline">
      <div
        v-for="message in messages"
        :key="message.id"
        class="timeline-item comment"
        :class="{'drcy-chat-failed': message.status === 'failed'}"
      >
        <a class="timeline-avatar" :href="avatarHref(message)">
          <img class="ui avatar" :src="avatarSrc(message)" width="40" height="40" alt="">
        </a>
        <div class="content comment-container">
          <div class="comment-header" role="heading" aria-level="3">
            <div class="comment-header-left tw:flex tw:items-center">
              <span class="text grey muted-links">
                <strong class="tw:text-text-dark">{{ authorName(message) }}</strong>
              </span>
            </div>
          </div>
          <div class="ui attached segment comment-body" role="article">
            <!-- Streamed/final answer. Kept in the DOM (v-show) so its render
                 target is stable while chunks arrive. -->
            <div v-show="message.content" :ref="(el) => setBubble(message.id, el as Element | null)"></div>
            <!-- Busy state: the animation until text starts, and a single live
                 line for the tool DRCY is running right now. -->
            <template
              v-if="message.role === 'assistant' && (message.status === 'pending' || message.status === 'running')"
            >
              <img v-if="!message.content" :src="thinkingSrc" class="drcy-chat-thinking-anim" alt="DRCY is thinking…">
              <div v-if="message.activity" class="drcy-chat-tool" :class="{'tw:mt-2': message.content}">
                Tool: {{ message.activity }}
              </div>
            </template>
            <!-- Once the turn is done, summarize how many tools ran. -->
            <div
              v-if="message.role === 'assistant' && (message.status === 'completed' || message.status === 'failed') && message.toolCount"
              class="drcy-chat-toolcount tw:mt-2"
            >
              {{ message.toolCount }} tool call{{ message.toolCount === 1 ? '' : 's' }}
            </div>
          </div>
        </div>
      </div>
     </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
.drcy-chat-messages {
  min-height: 12rem;
  max-height: 60vh;
  overflow-y: auto;
}
.drcy-chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 15rem;
  text-align: center;
  padding: 1.5rem 1rem;
}
.drcy-chat-empty-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
}
.drcy-chat-empty-title {
  font-size: 1.15em;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}
.drcy-chat-empty-subtitle {
  color: var(--color-text-light-2);
  max-width: 34rem;
}
.drcy-chat-empty-examples {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
}
.drcy-chat-thinking-anim {
  display: block;
  height: 72px;
  width: auto;
}
.drcy-chat-tool {
  color: var(--color-text-light-2);
  font-size: 0.85em;
  font-family: var(--fonts-monospace);
}
.drcy-chat-toolcount {
  color: var(--color-text-light-3);
  font-size: 0.8em;
}
.drcy-chat-failed .comment-body {
  border-color: var(--color-red);
}
</style>

<!-- Unscoped: print isolation, plus the repo-level branch picker which lives in
     the page template (outside this component), so it can't be scoped. -->
<style> /* eslint-disable-line vue-scoped-css/enforce-style-type */
/* eslint-disable vue-scoped-css/no-unused-selector */

@media print {
  body * {
    visibility: hidden;
  }
  .drcy-chat-panel,
  .drcy-chat-panel * {
    visibility: visible;
  }
  .drcy-chat-panel {
    position: absolute;
    inset: 0;
    margin: 0;
    border: none;
    box-shadow: none;
  }
  .drcy-chat-toolbar,
  .drcy-chat-end,
  .drcy-chat-compose-area {
    display: none !important;
  }
  .drcy-chat-messages {
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>
